import { donoDe } from "@/lib/api";
import { comDe } from "@/lib/escritorioInfo";
import { Link } from "react-router-dom";
import { ClipboardList, ExternalLink, Eye } from "lucide-react";
import MarcarFeito from "@/components/MarcarFeito";
import { BadgeParticular, BadgeCompartilhado } from "@/components/Particular";
import { EventTypeBadge } from "@/components/NewEventDialog";
import { formatCourtDistrict, formatParties, publicationDate, formatDate } from "@/lib/format";

const WEEKDAYS = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const MONTHS = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

// "2026-10-06" -> { wd: "SEG", day: "06", mon: "OUT", dm: "06 OUT" } (sem fuso: lê o texto).
export function dateParts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return { wd: WEEKDAYS[d.getDay()], day: m[3], mon: MONTHS[d.getMonth()], dm: `${m[3]} ${MONTHS[d.getMonth()]}` };
}

// Lote 94 — "já ocorreu": audiência/compromisso cuja data e hora já passaram (prazo não entra: prazo vencido
// é alerta no Painel, só some quando é marcado como feito). Sem hora, só passa no dia seguinte.
// Lote 109 (Robson, 24/10/2026): o compromisso de HOJE não "envelhece" sozinho depois do horário — mantém cor e
// quadro até a baixa manual (✓) ou até virar o dia; só data anterior a hoje é "já ocorreu".
export function jaPassou(hearing, now = new Date()) {
  const h = hearing || {};
  if ((h.event_type || "audiencia") === "prazo" || !h.date) return false;
  const pad = (n) => String(n).padStart(2, "0");
  const hoje = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return h.date < hoje;
}

const PHASE_LABELS = { inicial: "Inicial", instrucao: "Instrução", julgamento: "Julgamento", outro: "Outro" };
export const SOURCE_LABELS = {
  manual: "Manual",
  djen: "DJEN",
  shared: "Compartilhado",
  datajud: "DataJud",
  ata_extraction: "Extração de ata",
  djen_auto_prazo: "Prazo automático",
  auto_prazo_audiencia: "Prazo de audiência",
};

// Sub-etiqueta do tipo: fase (audiência), tipo de prazo ou assunto do compromisso "Outro".
export const typeSubLabel = (hearing) => {
  const h = hearing || {};
  const type = h.event_type || "audiencia";
  if (type === "audiencia") return h.hearing_phase ? (PHASE_LABELS[h.hearing_phase] || h.hearing_phase) : null;
  if (type === "prazo") return h.deadline_type || null;
  if (type === "reuniao") return null; // Lote 112: o subtipo agora é a própria etiqueta
  return null;
};

// Botão de ação por tipo (Lote 69): Audiência -> Preparar; Prazo -> Abrir; Outro (perícia etc.) -> Ver.
const ACTION_BY_TYPE = {
  audiencia: { label: "Preparar", icon: ClipboardList, primary: true },
  prazo: { label: "Abrir", icon: ExternalLink, primary: false },
  reuniao: { label: "Ver", icon: Eye, primary: false },
};

// Botão "Abrir/Preparar/Ver" — o mesmo desenho no celular (card) e no computador (tabela da Agenda).
export const destinoItem = (h) => (h.compartilhado ? `/compartilhamentos?aba=recebidos&abrir=${h.compartilhado.share_id}` : comDe(`/audiencia/${h.pub_id}`, donoDe(h.pub_id)));

export function AcaoAbrir({ h, className = "" }) {
  const type = h.hearing?.event_type || "audiencia";
  const action = ACTION_BY_TYPE[type] || ACTION_BY_TYPE.audiencia;
  const ActionIcon = action.icon;
  return (
    <Link
      to={destinoItem(h)}
      data-testid={`open-hearing-${h.pub_id}`}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 h-9 text-xs font-semibold border ${
        action.primary ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-800 border-stone-300"
      } ${className}`}
    >
      <ActionIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> {action.label}
    </Link>
  );
}

// Linha de apoio: vara/comarca (+ fase) para audiência; senão o tipo de prazo/assunto e a origem.
function supportLine(h) {
  const hearing = h.hearing || {};
  const type = hearing.event_type || "audiencia";
  const court = formatCourtDistrict(hearing.court, hearing.court_district);
  const sub = typeSubLabel(hearing);
  const src = h.source ? (SOURCE_LABELS[h.source] || h.source) : null;
  const parts = type === "audiencia" ? [court !== "—" ? court : null, sub] : [sub, src];
  return parts.filter(Boolean).join(" · ") || src || "";
}

// Caixa "Dia/Hora" das tabelas do desktop (Lote 70): fundo cinza suave e traço vertical à
// direita — o mesmo desenho do bloco de data/hora dos cards do celular.
export function DiaHoraBox({ label, time, isToday = false }) {
  return (
    <div
      className={`inline-flex flex-col justify-center min-w-[6.5rem] rounded-md border-r-2 px-3 py-1.5 tabular-nums ${
        isToday ? "bg-transparent border-gold-500/60" : "bg-stone-100 border-stone-300"
      }`}
    >
      <div className="text-[10px] font-bold tracking-wider uppercase text-stone-500 whitespace-nowrap leading-tight">{label}</div>
      <div className={`font-display text-lg font-bold leading-tight ${isToday ? "text-gold-800" : "text-stone-900"}`}>{time || "—"}</div>
    </div>
  );
}

// Card de compromisso (mobile): data/hora à esquerda, tipo + partes + apoio ao centro, ação à direita.
export default function CompromissoCard({ h, isToday, onToggleDone, muted = false, notValidated = false, extraBadges = null, toggleNode = null }) {
  const hearing = h.hearing || {};
  const type = hearing.event_type || "audiencia";
  const parts = dateParts(hearing.date);
  const to = destinoItem(h);
  const passou = jaPassou(hearing);
  if (passou) { isToday = false; notValidated = false; }
  const baixavel = !!toggleNode || (!!onToggleDone && (type === "prazo" || h.prazo_done === true || (isToday && !passou)));
  return (
    <div
      data-testid={`row-hearing-${h.pub_id}`}
      data-passou={passou ? "1" : "0"}
      className={`rounded-lg border p-3 ${muted ? "opacity-60" : ""} ${
        passou ? "bg-stone-50 border-stone-200 opacity-80" : isToday ? "bg-white border-gold-500" : notValidated ? "bg-blue-50 border-blue-200" : "bg-white border-stone-200"
      }`}
    >
      <div className="flex items-stretch gap-3">
      <Link to={to} className="flex items-stretch gap-2 min-w-0 flex-1" aria-label="Abrir detalhes do compromisso">
        <div className="w-[clamp(5.75rem,25vw,8rem)] shrink-0 border-r border-stone-200 pr-2 flex flex-col justify-center">
          <div className="text-[clamp(0.625rem,2.8vw,0.75rem)] font-bold tracking-wider uppercase text-stone-500 tabular-nums leading-tight whitespace-nowrap">
            {isToday ? "HOJE" : parts ? `${parts.wd} · ${parts.dm}` : "—"}
          </div>
          <div className={`font-display text-[clamp(1.25rem,6vw,1.6rem)] font-bold tabular-nums leading-tight ${passou ? "text-stone-400" : isToday ? "text-gold-800" : "text-stone-900"}`}>
            {hearing.time || "—"}
          </div>
          {passou && <div className="text-[10px] font-bold tracking-wider uppercase text-stone-400 leading-tight" data-testid={`ja-ocorreu-${h.pub_id}`}>Já ocorreu</div>}
        </div>
        <div className="min-w-0 flex-1 flex flex-col justify-center gap-0.5">
          <div className="flex items-center gap-1.5">
            <EventTypeBadge hearing={hearing} passou={passou} />
            {extraBadges}
            {(h.particular || h.particular_ef) && <BadgeParticular testid={`particular-${h.pub_id}`} />}
            {h.compartilhado && <BadgeCompartilhado de={h.compartilhado.de_nome} testid={`compartilhado-${h.pub_id}`} />}
          </div>
          <div className="text-sm font-semibold text-stone-900 truncate">
            {formatParties(hearing.parties) || hearing.case_number || "—"}
          </div>
          <div className="text-xs text-stone-500 truncate">{supportLine(h)}</div>
          {h.responsavel_nome && <div className="text-xs text-stone-500 truncate" data-testid={`resp-${h.pub_id}`}>Resp.: {h.responsavel_nome}{h.tambem_em?.length > 0 ? ` · também na conta de ${h.tambem_em.map((t) => t.nome).join(", ")}` : ""}</div>}
          {type !== "reuniao" && publicationDate(h) && (
            <div className="text-[11px] text-stone-400 truncate" data-testid={`publicado-em-${h.pub_id}`}>
              Publ. {formatDate(publicationDate(h)).slice(0, 5)}
            </div>
          )}
        </div>
      </Link>
      <div className="flex flex-col items-stretch justify-center gap-1.5 shrink-0 self-center">
        <AcaoAbrir h={h} />
        {baixavel && (toggleNode ?? <MarcarFeito coluna h={h} onToggleDone={onToggleDone} />)}
      </div>
      </div>
    </div>
  );
}
